import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Logo from "../components/Logo";
import { Loader2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptInvite() {
  const { token } = useParams();
  const [invite, setInvite] = useState(undefined); // undefined=loading, null=invalid
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/admins/invites/token/${token}`)
      .then(({ data }) => setInvite(data))
      .catch(() => setInvite(null));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) {
      setErr("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      setErr("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admins/invites/token/${token}/accept`,
        { name: name.trim(), password },
        { withCredentials: true }
      );
      toast.success("Welcome aboard! Signing you in…");
      window.location.href = "/admin";
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not accept the invitation");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#FAFAFA] p-4">
      <div className="w-full max-w-md bg-white border border-gray-300 rounded-sm p-8" data-testid="accept-invite-page">
        <div className="flex items-center gap-3 mb-6">
          <Logo size="sm" />
          <div>
            <div className="font-black tracking-tighter text-lg leading-none">AVIDITY</div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500">Cleaning Services</div>
          </div>
        </div>

        {invite === undefined && (
          <div className="flex items-center gap-2 text-gray-500 py-8 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking invitation…
          </div>
        )}

        {invite === null && (
          <div className="py-6 text-center space-y-2" data-testid="invite-invalid">
            <h2 className="text-xl font-black tracking-tight">Invitation not valid</h2>
            <p className="text-sm text-gray-600">
              This invitation link has expired, was revoked, or has already been used.
              Ask an administrator to send you a new one.
            </p>
          </div>
        )}

        {invite && (
          <form onSubmit={submit} className="space-y-4">
            <div className="flex items-center gap-2 text-[#002FA7]">
              <ShieldCheck className="w-5 h-5" />
              <h2 className="text-xl font-black tracking-tight">Administrator invitation</h2>
            </div>
            <p className="text-sm text-gray-600">
              {invite.inviter_name ? <strong>{invite.inviter_name}</strong> : "An administrator"} invited{" "}
              <strong>{invite.email}</strong> to join as an administrator. Set your details to activate
              your account.
            </p>
            <div>
              <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1" htmlFor="invite-name">
                Full name
              </label>
              <input
                id="invite-name"
                data-testid="invite-name-input"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-gray-300 px-3 py-2 rounded-sm focus:border-[#002FA7] outline-none"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1" htmlFor="invite-password">
                Password (min 8 characters)
              </label>
              <input
                id="invite-password"
                data-testid="invite-password-input"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-gray-300 px-3 py-2 rounded-sm focus:border-[#002FA7] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1" htmlFor="invite-confirm">
                Confirm password
              </label>
              <input
                id="invite-confirm"
                data-testid="invite-confirm-input"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full border border-gray-300 px-3 py-2 rounded-sm focus:border-[#002FA7] outline-none"
              />
            </div>
            {err && (
              <div data-testid="invite-error" className="px-3 py-2 border border-red-300 bg-red-50 text-red-800 text-sm rounded-sm">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="invite-accept-button"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#002FA7] text-white font-bold tracking-wide hover:bg-[#002277] disabled:opacity-50 rounded-sm transition"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Activate admin account"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
